import React from 'react';
import { format } from 'date-fns';
import { Article } from '../../../contexts/ArticlesContext';

interface FilterState {
  search: string;
  analysisStatus: 'all' | 'analyzed' | 'pending' | 'unanalyzed' | 'not_relevant';
  minRiskScore: number;
  threatCategory: string;
  sortBy: 'date' | 'risk' | 'title';
  sortOrder: 'asc' | 'desc';
}

interface ArticlesTableProps {
  articles: Article[];
  filteredArticles: Article[];
  filters: FilterState;
  selectedArticleIds: Set<number>;
  selectedSourceId: number | null;
  lastAnalyzedArticleId: number | null;
  analyzingArticleId: number | null;
  deletingAnalysisId: number | null;
  loading: boolean;
  sources: { id: number; name: string }[];
  onSort: (column: 'date' | 'risk' | 'title') => void;
  onSelectAll: () => void;
  onSelectArticle: (id: number) => void;
  onViewArticle: (article: Article) => void;
  onAnalyzeArticle: (id: number) => void;
  onDeleteAnalysis: (id: number) => void;
  onCompareArticle: (article: Article) => void;
  getSortIcon: (column: 'date' | 'risk' | 'title') => string;
}

export function ArticlesTable({
  articles,
  filteredArticles,
  filters,
  selectedArticleIds,
  selectedSourceId,
  lastAnalyzedArticleId,
  analyzingArticleId,
  deletingAnalysisId,
  loading,
  sources,
  onSort,
  onSelectAll,
  onSelectArticle,
  onViewArticle,
  onAnalyzeArticle,
  onDeleteAnalysis,
  onCompareArticle,
  getSortIcon
}: ArticlesTableProps) {
  const isAllSelected = filteredArticles.length > 0 && 
    filteredArticles.every(article => selectedArticleIds.has(article.id));

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="tc-bg-secondary">
          <tr>
            <th className="pl-6 pr-3 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={onSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
              ID
            </th>
            <th 
              className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => onSort('title')}
            >
              <div className="flex items-center justify-between">
                Title
                <span className="ml-1">{getSortIcon('title')}</span>
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
              Source
            </th>
            <th 
              className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => onSort('date')}
            >
              <div className="flex items-center justify-between">
                Date
                <span className="ml-1">{getSortIcon('date')}</span>
              </div>
            </th>
            <th 
              className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => onSort('risk')}
            >
              <div className="flex items-center justify-between">
                Analysis
                <span className="ml-1">{getSortIcon('risk')}</span>
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="tc-bg-primary divide-y divide-gray-200">
          {loading && filteredArticles.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-6 py-4 text-center tc-text-secondary">
                Loading articles...
              </td>
            </tr>
          ) : filteredArticles.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-6 py-4 text-center tc-text-secondary">
                No articles found
                {selectedSourceId && (
                  <span> from {sources.find(s => s.id === selectedSourceId)?.name}</span>
                )}
                {filters.search && <span> matching "{filters.search}"</span>}
                {filters.minRiskScore > 0 && <span> with risk ≥ {filters.minRiskScore}</span>}
              </td>
            </tr>
          ) : (
            filteredArticles.map((article) => (
              <tr 
                key={article.id} 
                data-article-id={article.id}
                className={`tc-hover ${lastAnalyzedArticleId === article.id ? 'bg-blue-50' : ''}`}
              >
                <td className="pl-6 pr-3 py-4">
                  <input
                    type="checkbox"
                    checked={selectedArticleIds.has(article.id)}
                    onChange={() => onSelectArticle(article.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>
                <td className="px-2 py-4 whitespace-nowrap text-xs tc-text-tertiary">
                  {article.id}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium tc-text-primary truncate max-w-md">
                    {article.title}
                  </div>
                  {article.content && (
                    <div className="text-xs tc-text-tertiary truncate max-w-md mt-1">
                      {article.content.substring(0, 100)}...
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm tc-text-secondary">
                  {article.source.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm tc-text-secondary">
                  {format(
                    new Date(article.publishedAt || article.scrapedAt), 
                    new Date(article.publishedAt || article.scrapedAt).getFullYear() !== new Date().getFullYear() 
                      ? 'MMM dd, yyyy HH:mm' 
                      : 'MMM dd, HH:mm'
                  )}
                </td>
                <td className="px-6 py-4">
                  <div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      article.hasAnalysis 
                        ? 'bg-green-100 text-green-800' 
                        : article.hasPendingJob 
                        ? 'bg-orange-100 text-orange-800'
                        : article.relevanceChecked && !article.isRelevant
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {article.hasAnalysis ? '✅ Analyzed' : article.hasPendingJob ? '🔄 Processing' : article.relevanceChecked && !article.isRelevant ? '❌ Not Relevant' : '⏳ Pending'}
                    </span>
                    {article.relevanceChecked && !article.isRelevant && article.relevanceScore !== undefined && (
                      <div className="text-xs tc-text-secondary mt-1">
                        Score: {article.relevanceScore.toFixed(2)}
                      </div>
                    )}
                    {article.extractionFailed && (
                      <div className="text-xs text-red-600 mt-1">
                        ⚠️ Extraction failed
                      </div>
                    )}
                    {article.latestAnalysis && (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-2 text-xs font-medium tc-text-primary">
                          <span>Risk: {article.latestAnalysis.overallRiskScore}/10</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            article.latestAnalysis.alertLevel === 'CRITICAL' ? 'bg-red-500 text-white' :
                            article.latestAnalysis.alertLevel === 'HIGH' ? 'bg-orange-500 text-white' :
                            article.latestAnalysis.alertLevel === 'MEDIUM' ? 'bg-yellow-500 text-black' :
                            'bg-green-500 text-white'
                          }`}>
                            {article.latestAnalysis.alertLevel}
                          </span>
                        </div>
                        {article.latestAnalysis.categoryScores && (
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(article.latestAnalysis.categoryScores)
                              .filter(([_, score]) => (score as number) > 0)
                              .map(([category, score]) => (
                                <span key={category} className={`px-1.5 py-0.5 text-xs rounded ${
                                  (score as number) >= 9 ? 'bg-red-100 text-red-800' :
                                  (score as number) >= 7 ? 'bg-orange-100 text-orange-800' :
                                  (score as number) >= 5 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {category}: {score}/10
                                </span>
                              ))
                            }
                          </div>
                        )}
                        {article.latestAnalysis.threatActorMentions && article.latestAnalysis.threatActorMentions.length > 0 && (
                          <div className="text-xs tc-text-secondary">
                            Actors: {article.latestAnalysis.threatActorMentions.join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => onViewArticle(article)}
                      className="tc-link p-1 hover:bg-gray-100 rounded"
                      title="View article details"
                    >
                      👁️
                    </button>
                    {!article.hasPendingJob && (
                      <button
                        onClick={() => onAnalyzeArticle(article.id)}
                        disabled={analyzingArticleId === article.id}
                        className={`tc-link p-1 hover:bg-gray-100 rounded ${
                          analyzingArticleId === article.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title={article.hasAnalysis ? 'Force re-analyze this article' : 'Analyze this article'}
                      >
                        {analyzingArticleId === article.id ? '⏳' : (article.hasAnalysis ? '🔄' : '🤖')}
                      </button>
                    )}
                    {article.hasAnalysis && !article.hasPendingJob && (
                      <button
                        onClick={() => onDeleteAnalysis(article.id)}
                        disabled={deletingAnalysisId === article.id}
                        className={`tc-link p-1 hover:bg-red-100 rounded text-red-600 hover:text-red-800 ${
                          deletingAnalysisId === article.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title="Delete saved analysis for this article"
                      >
                        {deletingAnalysisId === article.id ? '⏳' : '🗑️'}
                      </button>
                    )}
                    <button
                      onClick={() => onCompareArticle(article)}
                      className="tc-link p-1 hover:bg-blue-100 rounded text-blue-600 hover:text-blue-800 opacity-50 hover:opacity-100 transition-opacity"
                      title="Compare different AI models on this article"
                    >
                      ⚖️
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
